import Navbar from '../../../../core/components/navbar/navbar';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import UserTitle from '../components/user_title';
import AreaOfDevelopment from '../components/area_of_development';
import ThemeConstants from '../../../../config/theme/theme_constants';
import UnlockCard from '../components/lock_cards/unlock_card';
import LockCard from '../components/lock_cards/lock_card';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { UserInterface } from '../../../../core/components/user/domain/interfaces/user_interface';
import { ThemeInterface } from '../../domain/interface/theme_interface';
import { useGetThemeByCompanyId } from '../../domain/hooks/use_theme';
import { AnimatePresence, motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { getCurrentUserTheme, getUserThemeByThemeId } from '../../domain/utils/get_current_user_theme';
import { useTranslation } from '../../../../i18n/useTranslation';
import PartnersSection from '../../../../core/components/partners_section';

const StartPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = ThemeConstants();
  const navigate = useNavigate();
  const { experience, company, course_theme, careerStatus} = useOutletContext<UserInterface>();

  const { data: themes } = useGetThemeByCompanyId(company?.id);

  useEffect(() => {
    const currentTheme = getCurrentUserTheme(course_theme, company?.id);
    if (currentTheme && company?.permissions.course === true) {
      const themeId = typeof currentTheme._themeId === 'string' ? currentTheme._themeId : currentTheme._themeId!._id;
      navigate(`/career/${themeId}/resources`, { replace: true });
    }
  }, [course_theme, navigate, company?.id, company?.permissions.course]);


  const isFinished = useCallback((themeId: string) => {
    return getUserThemeByThemeId(course_theme, themeId, company?.id)?.status === 'completed';
  }, [course_theme, company?.id]);

  // Only training courses are locked behind points; the media library is always open.
  const isLocked = useMemo(() => {
    return company?.permissions.course === true && careerStatus === 'start';
  }, [careerStatus, company?.permissions.course]);
  const prevLockedRef = useRef(isLocked);
  const hasMountedRef = useRef(false);

  const requiredExp = useMemo(() => {
    return company?.limits?.pointsCareerOpen ?? 50;
  }, [company?.limits?.pointsCareerOpen]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      prevLockedRef.current = isLocked;
      return;
    }
    if (prevLockedRef.current && !isLocked) {
      const colors = [theme.UvibesPink, theme.UvibesYellow, theme.UvibesBlue, theme.UvibesGreen, theme.UvibesOrange];
      const end = Date.now() + 900;
      const frame = () => {
        confetti({
          particleCount: 5,
          startVelocity: 45,
          spread: 70,
          ticks: 200,
          origin: { x: 0.5, y: 0.55 },
          colors,
          scalar: 1.05,
          disableForReducedMotion: true,
        });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    }
    prevLockedRef.current = isLocked;
  }, [isLocked, theme]);

  return (
    <div className="min-h-dvh pt-[5.6dvh] max-lg:px-[3%] lg:px-[1%] flex flex-col gap-8 items-center relative max-md:pb-32 md:pb-29">
      <AnimatePresence>
        {isLocked && (
          <motion.div
            key="lock-overlay"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{
              opacity: 0,
              scale: 1.4,
              filter: 'blur(12px)',
              transition: { duration: 0.55, ease: [0.34, 1.56, 0.64, 1] },
            }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="contents"
          >
            {experience.totalExp >= requiredExp ? <UnlockCard /> : <LockCard />}
          </motion.div>
        )}
      </AnimatePresence>

      <UserTitle
        subtitle={company?.permissions.course === true ? t('course.start.welcome_course') : t('course.start.welcome_library')}
        description={company?.permissions.course === true ? t('course.start.choose_axis') : t('course.start.choose_theme')}
      />

      <motion.div
        className="flex flex-col flex-1 gap-4 w-full max-w-115 mx-auto"
        initial={false}
        animate={{ filter: isLocked ? 'blur(24px)' : 'blur(0px)' }}
        transition={{ duration: 0.75, ease: 'easeInOut', delay: isLocked ? 0 : 0.25 }}
      >
        <p className="text-base font-bold text-black" style={{ color: theme.UvibesGray }}>{company?.permissions.course === true ? t('course.start.my_axes') : t('course.start.my_themes')}</p>
        <motion.div
          className='flex flex-col gap-5 items-center w-full'
          initial={false}
          animate={isLocked ? 'locked' : 'unlocked'}
          variants={{
            locked: { transition: { staggerChildren: 0 } },
            unlocked: { transition: { staggerChildren: 0.08, delayChildren: 0.35 } },
          }}
        >
          {themes?.map((axis: ThemeInterface, index: number) => (
            <motion.div
              key={`area_of_development_${index}`}
              className="w-full flex justify-center"
              variants={{
                locked: { scale: 1 },
                unlocked: {
                  scale: [1, 1.04, 1],
                  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
                },
              }}
            >
              <AreaOfDevelopment
                id={axis._id}
                name={axis.name}
                img={axis.picture}
                backgroundColor={axis.color}
                mode={isFinished(axis._id) ? 'finished' : 'waiting'}
              />
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      <PartnersSection/>

      <div className='fixed flex justify-center bottom-6 w-full px-3 z-50'>
        <Navbar/>
      </div>
    </div>
  );
};

export default StartPage;
